// src/pages/ExpensePurchaseOrder/EpoPartialPayments.js
//
// Round 19 — EPO Partial Payments page. Restores the legacy
// Dashboard_ForChatGPT/Projects/epoApprove.vb partial-payment workflow:
//   - View running total paid vs EPO grand total
//   - Per-line "Add Payment" button (Finance/Babu/IT-Admin only)
//   - Per-row Void action on existing payments
//   - Chronological log of all payments for the EPO
//
// Routes: /epo/:epoNumber/partial-payments
// Access: gated to Finance / Babu / IT-Admin via isFinanceFlavor in JSX
// (the backend enforces the same gate on mutations; reads are open).
//
// Mirrors legacy:
//   PartialPayment.GetByEpoNumLineId / GetTotalByEpoAndLine / GetTotalByEpo
//   new PartialPayment { ... }.Insert()
//   row.VoidEntry()

import React, { useEffect, useState, useCallback } from "react";
import {
    Container, Row, Col, Card, CardBody, Table, Button, Badge, Spinner, Alert,
    Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, FormGroup
} from "reactstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import { DashboardPageHeader } from "../../Components/Common/DashboardKit";
import useAuth from "../Personal Attendance/useAuth";
import api from "../../api";
import {
    listPartialPayments, getPartialPaymentTotal,
    createPartialPayment, voidPartialPayment,
    markLinePaid  // R20
} from "../../services/epoPartialPaymentApi";

// --- helpers --------------------------------------------------------------

const pick = (obj, ...keys) => {
    for (const k of keys) {
        if (obj != null && obj[k] !== undefined && obj[k] !== null) return obj[k];
    }
    return undefined;
};

const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleString(); } catch { return "—"; }
};

const fmtMoney = (n) => {
    const v = Number(n ?? 0);
    return Number.isFinite(v) ? `$${v.toFixed(2)}` : "—";
};

const errMsg = (e) =>
    e?.response?.data?.message ??
    e?.response?.data?.Message ??
    e?.message ??
    "Action failed.";

// --- component ------------------------------------------------------------

const EpoPartialPayments = () => {
    const { epoNumber } = useParams();
    const navigate = useNavigate();
    const auth = useAuth();
    const isFinanceFlavor = !!(auth?.isBabu || auth?.isFinance || auth?.isItAdmin);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(false);

    const [header, setHeader] = useState(null);
    const [lines, setLines] = useState([]);
    const [payments, setPayments] = useState([]);
    const [totalPaid, setTotalPaid] = useState(0);

    // Add Payment modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [modalLine, setModalLine] = useState(null);    // EpoLine being paid
    const [formAmount, setFormAmount] = useState("");
    const [formNote, setFormNote] = useState("");
    const [formErr, setFormErr] = useState(null);

    const reload = useCallback(async () => {
        if (!epoNumber) return;
        setLoading(true); setError(null);
        try {
            // Three parallel reads — EPO detail (for header + lines), payments
            // list, and the running total. Each is independent.
            const [epoRes, payList, payTotal] = await Promise.all([
                api.get(`Epo/${epoNumber}`),
                listPartialPayments(epoNumber),
                getPartialPaymentTotal(epoNumber),
            ]);
            const hdr = epoRes.data?.header ?? epoRes.data?.Header ?? null;
            const lns = epoRes.data?.lines  ?? epoRes.data?.Lines  ?? [];
            setHeader(hdr);
            setLines(Array.isArray(lns) ? lns : []);
            setPayments(Array.isArray(payList) ? payList : []);
            setTotalPaid(Number(payTotal?.Total ?? payTotal?.total ?? 0));
        } catch (e) {
            setError(errMsg(e));
        } finally {
            setLoading(false);
        }
    }, [epoNumber]);

    useEffect(() => { reload(); }, [reload]);

    // Open the Add Payment modal for a specific line.
    const openAdd = (line) => {
        setFormErr(null);
        setFormAmount("");
        setFormNote("");
        setModalLine(line);
        setModalOpen(true);
    };
    const closeModal = () => { if (!busy) setModalOpen(false); };

    const submitPayment = async () => {
        setFormErr(null);
        const amt = Number(formAmount);
        // Round EPO-Pay-1 (owner request #6): negative = credit memo /
        // vendor refund. Only zero/blank/non-numeric is invalid.
        if (!Number.isFinite(amt) || amt === 0) {
            setFormErr("Amount must be a non-zero number (negative = credit memo / refund).");
            return;
        }
        // Go-live parity (2026-09-24): EPO#N2 — legacy required a payment note
        // (the server refuses a blank one too).
        if (!String(formNote || "").trim()) {
            setFormErr("A note is required (reference number, check #, card or invoice).");
            return;
        }
        const lineNum = pick(modalLine, "LineNum", "lineNum");
        if (lineNum == null) {
            setFormErr("Line number is missing.");
            return;
        }
        setBusy(true);
        try {
            const result = await createPartialPayment(epoNumber, {
                lineNum: Number(lineNum),
                amount: amt,
                note: formNote || "",
            });
            // Round EPO-Final — the backend auto-finalizes the EPO as Paid
            // when this payment lands the authoritative remaining liability
            // on exactly $0.00 (no separate Mark Paid click). Overpayments
            // are surfaced and are never auto-finalized.
            const autoPaid = !!pick(result, "autoPaid", "AutoPaid");
            const overpaid = !!pick(result, "overpaid", "Overpaid");
            const rem = pick(result, "remaining", "Remaining");
            if (autoPaid) {
                setNotice(`Payment of ${fmtMoney(amt)} added — remaining liability is $0.00. EPO #${epoNumber} was automatically marked Paid/Processed.`);
            } else if (overpaid) {
                setNotice(`Payment of ${fmtMoney(amt)} added — WARNING: active payments now exceed the EPO Grand Total (remaining ${fmtMoney(rem)}). The EPO was NOT marked Paid.`);
            } else {
                setNotice(`Payment of ${fmtMoney(amt)} added to line ${lineNum}.`);
            }
            setModalOpen(false);
            await reload();
        } catch (e) {
            setFormErr(errMsg(e));
        } finally {
            setBusy(false);
        }
    };

    // R20 — toggle EpoLine.Paid for a specific line. Same gate as create/void.
    const handleToggleLinePaid = async (line, nextPaid) => {
        const ln = pick(line, "LineNum", "lineNum");
        setBusy(true); setError(null);
        try {
            await markLinePaid(epoNumber, ln, { paid: nextPaid });
            setNotice(`Line ${ln} marked ${nextPaid ? "Paid" : "Unpaid"}.`);
            await reload();
        } catch (e) {
            setError(errMsg(e));
        } finally {
            setBusy(false);
        }
    };

    const handleVoid = async (id) => {
        if (!window.confirm("Void this payment? This cannot be undone.")) return;
        setBusy(true); setError(null);
        try {
            await voidPartialPayment(epoNumber, id);
            setNotice(`Payment #${id} voided.`);
            await reload();
        } catch (e) {
            setError(errMsg(e));
        } finally {
            setBusy(false);
        }
    };

    // --- derived ---
    const grandTotal = Number(pick(header, "ApproxTotal", "approxTotal") ?? 0);
    const remaining = grandTotal - totalPaid;
    const vendor = pick(header, "Company", "company") ?? "—";
    const epoStatus = pick(header, "Status", "status") ?? "—";
    const paymentIssued = !!pick(header, "PaymentIssued", "paymentIssued");

    // Per-line paid totals (computed client-side from payments list).
    const paidByLine = {};
    for (const p of payments) {
        if (p.Void || p.void) continue;
        const ln = pick(p, "LineNum", "lineNum");
        const amt = Number(pick(p, "Amount", "amount") ?? 0);
        paidByLine[ln] = (paidByLine[ln] || 0) + amt;
    }

    // ---------- render ----------

    if (loading) {
        return (
            <div className="page-content">
                <Container fluid>
                    <DashboardPageHeader
                        title={`EPO #${epoNumber} — Partial Payments`}
                        breadcrumb={[{ label: "EPO", to: "/workflows?wf=epo" }, { label: `EPO #${epoNumber} — Partial Payments` }]}
                        breadcrumbItems={[{ label: "EPO", href: "/epo" }, { label: "Partial Payments" }]}
                    />
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    title={`EPO #${epoNumber} — Partial Payments`}
                    breadcrumb={[{ label: "EPO", to: "/workflows?wf=epo" }, { label: `EPO #${epoNumber} — Partial Payments` }]}
                    subtitle={`Vendor: ${vendor} · Status: ${epoStatus}${paymentIssued ? " · PROCESSED" : ""}`}
                    breadcrumbItems={[{ label: "EPO", href: "/epo" }, { label: "Partial Payments" }]}
                />

                {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
                {notice && <Alert color="success" toggle={() => setNotice(null)}>{notice}</Alert>}

                {!isFinanceFlavor && (
                    <Alert color="warning">
                        You can view partial payments but only Finance or IT Admin may add or void them.
                    </Alert>
                )}

                {/* Summary card — EPO grand total, paid total, remaining. */}
                <Card className="mt-3">
                    <CardBody>
                        <Row className="g-3">
                            <Col md={4}>
                                <div className="text-muted small">EPO Grand Total</div>
                                <div className="fs-4 fw-bold">{fmtMoney(grandTotal)}</div>
                            </Col>
                            <Col md={4}>
                                <div className="text-muted small">Paid So Far</div>
                                <div className="fs-4 fw-bold text-success">{fmtMoney(totalPaid)}</div>
                            </Col>
                            <Col md={4}>
                                <div className="text-muted small">Remaining</div>
                                <div className={`fs-4 fw-bold ${remaining <= 0 ? "text-success" : "text-warning"}`}>
                                    {fmtMoney(remaining)}
                                </div>
                            </Col>
                        </Row>
                        <div className="mt-3">
                            <Link to={`/epo/${epoNumber}/timeline`} className="btn btn-light btn-sm me-2">
                                <i className="ri-arrow-left-line align-bottom me-1"></i>Back to Timeline
                            </Link>
                            <Button color="light" size="sm" onClick={reload} disabled={busy}>
                                <i className="ri-refresh-line align-bottom me-1"></i>Refresh
                            </Button>
                        </div>
                    </CardBody>
                </Card>

                {/* Line items card — per-line totals + Add Payment button. */}
                <Card className="mt-3">
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="mb-0">Line Items</h5>
                            <span className="text-muted small">Add a payment to a specific line.</span>
                        </div>
                        {lines.length === 0 ? (
                            <div className="text-muted">No line items.</div>
                        ) : (
                            <div className="table-responsive epo-sticky-head">
                                <Table className="mb-0" striped bordered>
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 60 }}>Line</th>
                                            <th>Description</th>
                                            <th style={{ width: 110 }} className="text-end">Line Total</th>
                                            <th style={{ width: 110 }} className="text-end">Paid</th>
                                            <th style={{ width: 110 }} className="text-end">Remaining</th>
                                            <th style={{ width: 90 }} className="text-center">Mark Paid</th>{/* R20 */}
                                            <th style={{ width: 140 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lines.map((l) => {
                                            const ln = pick(l, "LineNum", "lineNum");
                                            const desc = pick(l, "Description", "description") ?? "";
                                            const ext = Number(pick(l, "ExtendedCost", "extendedCost") ?? 0);
                                            const paid = Number(paidByLine[ln] || 0);
                                            const rem = ext - paid;
                                            return (
                                                <tr key={ln}>
                                                    <td>{ln}</td>
                                                    <td>{desc}</td>
                                                    <td className="text-end">{fmtMoney(ext)}</td>
                                                    <td className="text-end">{fmtMoney(paid)}</td>
                                                    <td className={`text-end ${rem <= 0 ? "text-success" : ""}`}>
                                                        {fmtMoney(rem)}
                                                    </td>
                                                    {/* R20 — per-line Paid checkbox (legacy babuMode chkPaid). */}
                                                    <td className="text-center">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={!!pick(l, "Paid", "paid")}
                                                            disabled={!isFinanceFlavor || busy}
                                                            onChange={(e) => handleToggleLinePaid(l, e.target.checked)}
                                                            title={isFinanceFlavor
                                                                ? "Toggle line as Paid (Finance only)"
                                                                : "Finance only"}
                                                        />
                                                    </td>
                                                    <td>
                                                        {isFinanceFlavor && (
                                                            <Button
                                                                color="primary"
                                                                size="sm"
                                                                onClick={() => openAdd(l)}
                                                                disabled={busy}
                                                            >
                                                                <i className="ri-add-line align-bottom me-1"></i>Add Payment
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* Payments log card — chronological. */}
                <Card className="mt-3">
                    <CardBody>
                        <h5 className="mb-3">Payment History</h5>
                        {payments.length === 0 ? (
                            <div className="text-muted">No payments recorded yet.</div>
                        ) : (
                            <div className="table-responsive epo-sticky-head">
                                <Table className="mb-0" striped bordered>
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 60 }}>ID</th>
                                            <th style={{ width: 60 }}>Line</th>
                                            <th style={{ width: 130 }} className="text-end">Amount</th>
                                            <th>Note</th>
                                            <th style={{ width: 160 }}>Paid By</th>
                                            <th style={{ width: 160 }}>Timestamp</th>
                                            <th style={{ width: 90 }}>Status</th>
                                            <th style={{ width: 110 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments.map((p) => {
                                            const id = pick(p, "Id", "id");
                                            const ln = pick(p, "LineNum", "lineNum");
                                            const amt = Number(pick(p, "Amount", "amount") ?? 0);
                                            const note = pick(p, "Note", "note") ?? "";
                                            const paidByName = pick(p, "PaidByName", "paidByName") ?? "—";
                                            const ts = pick(p, "Timestamp", "timestamp");
                                            const voided = !!pick(p, "Void", "void");
                                            return (
                                                <tr key={id} className={voided ? "text-muted" : ""}>
                                                    <td>{id}</td>
                                                    <td>{ln}</td>
                                                    <td className="text-end">{fmtMoney(amt)}</td>
                                                    <td style={{ wordBreak: "break-word" }}>{note}</td>
                                                    <td>{paidByName}</td>
                                                    <td>{fmtDate(ts)}</td>
                                                    <td>
                                                        {voided
                                                            ? <Badge color="secondary">VOIDED</Badge>
                                                            : <Badge color="success">Active</Badge>}
                                                    </td>
                                                    <td>
                                                        {!voided && isFinanceFlavor && (
                                                            <Button
                                                                color="danger"
                                                                size="sm"
                                                                outline
                                                                onClick={() => handleVoid(id)}
                                                                disabled={busy}
                                                            >
                                                                Void
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* Add Payment modal */}
                <Modal isOpen={modalOpen} toggle={closeModal} centered>
                    <ModalHeader toggle={closeModal}>
                        Add Payment — EPO #{epoNumber}
                        {modalLine != null && (
                            <> · Line {pick(modalLine, "LineNum", "lineNum")}</>
                        )}
                    </ModalHeader>
                    <ModalBody>
                        {modalLine && (
                            <div className="mb-3 small text-muted">
                                <div><strong>Description:</strong> {pick(modalLine, "Description", "description") || "—"}</div>
                                <div>
                                    <strong>Line total:</strong>{" "}
                                    {fmtMoney(pick(modalLine, "ExtendedCost", "extendedCost"))}
                                    {" · "}
                                    <strong>Already paid:</strong>{" "}
                                    {fmtMoney(paidByLine[pick(modalLine, "LineNum", "lineNum")] || 0)}
                                </div>
                            </div>
                        )}
                        {formErr && <Alert color="danger" toggle={() => setFormErr(null)}>{formErr}</Alert>}

                        <FormGroup>
                            <Label for="ppAmount">Amount ($)</Label>
                            <Input
                                id="ppAmount"
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={formAmount}
                                onChange={(e) => setFormAmount(e.target.value)}
                                placeholder="0.00"
                                autoFocus
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="ppNote">Note <span className="text-danger">*</span></Label>
                            <Input
                                id="ppNote"
                                type="textarea"
                                rows={3}
                                value={formNote}
                                onChange={(e) => setFormNote(e.target.value)}
                                placeholder="Reference number, check #, etc."
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="light" onClick={closeModal} disabled={busy}>Cancel</Button>
                        <Button color="primary" onClick={submitPayment} disabled={busy}>
                            {busy ? "Saving…" : "Save Payment"}
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
    );
};

export default EpoPartialPayments;
